import { Box, Flex, IconButton, Spinner } from "@chakra-ui/react";
import Helper from "../../Utils/Helper";
import RoutingManager from "../../Utils/RoutingManager";
import { lazy, Suspense, useMemo, useState } from "react";
import { MdDownload } from "react-icons/md";

const DocumentView = lazy(() => import("./View/Document"));
const Markdown = lazy(() => import("./View/Markdown"));
const ImageView = lazy(() => import("./View/Image"));
const WordView = lazy(() => import("./View/Word"));
const ExcelView = lazy(() => import("./View/XLSX"));
const VideoView = lazy(() => import("./View/Video"));
const AudioView = lazy(() => import("./View/Audio"));

export default function FileView({
  Method = "G",
  File,
  Height = "500px",
  Width = "100%",
  AllowDownload = true,
  FolderFile = false,
  IsLocal = false,
  Lang: LangOverride = null,
}) {
  const { CurrentUser, Lang: CtxLang } = useMemo(() => {
    const LanguageKey = localStorage.getItem("lang") || "en";
    const Customerdata = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;
    return { CurrentUser: Customerdata, Lang: LanguageKey };
  }, []);
  const Lang = useMemo(
    () => LangOverride || CtxLang || {},
    [CtxLang, LangOverride],
  );
  const UserAccessToken =
    CurrentUser?.AccessToken ||
    CurrentUser?.UserAccessToken ||
    CurrentUser?.CustomerAccessToken ||
    "";
  const [IsLoading, SetIsLoading] = useState(false);
  const Url = useMemo(() => {
    if (!File) return null;
    if (IsLocal) return File?.FileUrl;
    if (FolderFile) {
      return `${RoutingManager.ApiUrl}/folder/file/${File.FileId}?X-T=${UserAccessToken}&Type=View`;
    }
    const url = new URL(`${RoutingManager.ApiUrl}/file`);
    url.searchParams.append("Id", File.FileId);
    url.searchParams.append("X-T", UserAccessToken);
    url.searchParams.append("Type", "View");
    url.searchParams.append("Method", Method);
    return url.href;
  }, [File, FolderFile, IsLocal, Method, UserAccessToken]);

  const HandleDownloadFile = async (FileId, FileName) => {
    try {
      SetIsLoading(true);
      let url = `${RoutingManager.ApiUrl}/folder/file/${File.FileId}`;
      if (!FolderFile) {
        const downloadUrl = new URL(`${RoutingManager.ApiUrl}/file`);
        downloadUrl.searchParams.append("Id", FileId);
        downloadUrl.searchParams.append("Method", Method);
        url = downloadUrl.href;
      }
      await Helper.DownloadFileFromLink(url, FileName);
    } finally {
      SetIsLoading(false);
    }
  };

  const ProcessView = useMemo(() => {
    if (!File || !Url)
      return (
        <Flex justifyContent="center" alignItems="center" w="100%" h={Height}>
          {Lang?.NO_FILE_PREVIEW || "Preview Not Available"}
        </Flex>
      );
    switch (File.FileType) {
      case "audio":
        return (
          <AudioView
            key={File.FileId}
            src={Url}
            title={File?.OriginalName}
            h={Height}
            IsLocal={IsLocal}
            Lang={Lang}
          />
        );
      case "video":
        return (
          <VideoView
            key={File.FileId}
            src={Url}
            title={File?.OriginalName}
            h={Height}
            IsLocal={IsLocal}
            Lang={Lang}
          />
        );
      case "excel":
        return (
          <ExcelView
            key={File.FileId}
            src={Url}
            title={File?.OriginalName}
            maxH={Height}
            IsLocal={IsLocal}
            Lang={Lang}
          />
        );
      case "word":
        return (
          <WordView
            key={File.FileId}
            src={Url}
            title={File?.OriginalName}
            rounded="lg"
            Width={Width}
            Height={Height}
            IsLocal={IsLocal}
            Lang={Lang}
          />
        );
      case "pdf":
        return (
          <DocumentView
            key={File.FileId}
            src={Url}
            title={File?.OriginalName}
            Type={File?.FileType}
            rounded="lg"
            h={Height}
            IsLocal={IsLocal}
            Lang={Lang}
          />
        );
      case "markdown":
        return (
          <Markdown
            key={File.FileId}
            src={Url}
            title={File?.OriginalName}
            Type={File?.FileType}
            rounded="lg"
            Width={Width}
            Height={Height}
            IsLocal={IsLocal}
            Lang={Lang}
          />
        );
      case "img":
        return (
          <ImageView
            key={File.FileId}
            IsLocal={IsLocal}
            src={Url}
            rounded="md"
            overflow="auto"
            h={{ base: "auto", md: Height }}
            Lang={Lang}
          />
        );
      default:
        return (
          <Flex justifyContent="center" alignItems="center" w="100%" h={Height}>
            {Lang?.NO_FILE_PREVIEW || "Preview Not Available"}
          </Flex>
        );
    }
  }, [File, Url, Height, Lang, IsLocal, Width]);

  if (!File) return null;

  return (
    <Box h={Height} w={Width} pos="relative">
      <Suspense
        fallback={
          <Flex
            justifyContent="center"
            alignItems="center"
            w={Width}
            h={Height}
          >
            <Spinner />
          </Flex>
        }
      >
        {ProcessView}
      </Suspense>
      {AllowDownload ? (
        <IconButton
          icon={<MdDownload />}
          onClick={() => HandleDownloadFile(File?.FileId, File?.OriginalName)}
          size="sm"
          isLoading={IsLoading}
          isDisabled={IsLoading}
          pos="absolute"
          top={0}
          right="0"
          rounded="full"
          className="Btn"
          aria-label="Download"
          title="Download"
          h={{ base: "1.8em !important", md: "1.8rem !important" }}
          w={{ base: "1.8em !important", md: "1.8rem !important" }}
          fontSize={{ base: "1.5em", md: "1.5rem" }}
          zIndex="2"
        />
      ) : null}
    </Box>
  );
}
